import React, {Fragment, useRef, useState} from 'react';
import Button from "../UI/Button";
import Modal from "../UI/Modal";
import ModalForm from "../UI/ModalForm";
import ModalFormInputRow from "../UI/ModalFormInputRow";
import ListEdit from "../UI/ListEdit";
import Box from "../UI/Box";
import Thumbnail from "../UI/Thumbnail";
import {toast} from "react-toastify";
import {useNavigate} from "react-router-dom";
import {useItems} from "../../hooks/useItems";
import {useItemCategories} from "../../hooks/useItemCategories";
import {useItemPropertyValueOptions} from "../../hooks/useItemPropertyValueOptions";
import {createItem, deleteItem, updateItem} from "../../api/itemApiClient";
import {buildChildrenByParent, flattenForSelect} from "../../utility/itemCategoryTree";
import PropertyRowsEditor, {emptyPropertyRow, propertySaveErrorMessage, toPropertyList} from "./PropertyRowsEditor";
import {compressImage} from "../../utility/compressImage";
import CustomQuillEditor from "../UI/CustomQuillEditor";

const emptyVariant = () => ({stockQuantity : 0, properties : [emptyPropertyRow()]});

const VariantRowsEditor = (props) => {
    const variants = props.variants;

    const handleStockChange = (index, value) => {
        const next = variants.map((variant, i) => i === index ? {...variant, stockQuantity : value} : variant);
        props.onChange(next);
    };

    const handlePropertiesChange = (index, properties) => {
        const next = variants.map((variant, i) => i === index ? {...variant, properties} : variant);
        props.onChange(next);
    };

    const handleAddVariant = () => {
        props.onChange([...variants, emptyVariant()]);
    };

    const handleRemoveVariant = (index) => {
        props.onChange(variants.filter((variant, i) => i !== index));
    };

    return (
        <div>
            {variants.map((variant, index) => (
                <div key={index} className="border rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                        <label className="font-bold">Variant {index + 1}</label>
                        {variants.length > 1 && (
                            <Button title="Remove variant" bgColor="red" handleClick={() => handleRemoveVariant(index)}/>
                        )}
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm text-gray-600 mb-1">Stock quantity</label>
                        <input
                            type="number"
                            value={variant.stockQuantity}
                            onChange={(event) => handleStockChange(index, event.target.value)}
                            className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                        />
                    </div>
                    <label className="block text-sm text-gray-600 mb-1">Properties</label>
                    <PropertyRowsEditor
                        properties={variant.properties}
                        onChange={(properties) => handlePropertiesChange(index, properties)}
                    />
                </div>
            ))}
            <Button title="+ Add variant" bgColor="gray" handleClick={handleAddVariant}/>
        </div>
    );
};

const ItemList = () => {
    const [showModal, setShowModal] = useState(false);
    const [isUpdate, setIsUpdate] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [submitCount, setSubmitCount] = useState(0);
    const [variantRows, setVariantRows] = useState([emptyVariant()]);
    const [filterItemCategoryId, setFilterItemCategoryId] = useState('');
    const [filterPropertyValueIds, setFilterPropertyValueIds] = useState([]);
    const [description, setDescription] = useState('');
    // A second submit while one is saving would save twice.
    const [saving, setSaving] = useState(false);
    // The chosen image, compressed as soon as it is picked (null when none is),
    // so the work overlaps with filling in the rest of the form.
    const chosenImageRef = useRef(null);
    const nameRef = useRef(null);
    const bruttoPriceRef = useRef(null);
    const isPublicRef = useRef(null);
    const orderRef = useRef(null);
    const itemCategoryIdRef = useRef(null);
    const filterPropertyValueRef = useRef(null);
    const navigate = useNavigate();

    const {items, pagination} = useItems(filterItemCategoryId || null, filterPropertyValueIds, page, pageSize, submitCount);
    const {itemCategories} = useItemCategories(submitCount);
    const {propertyValueOptions} = useItemPropertyValueOptions(submitCount);

    const byParent = buildChildrenByParent(itemCategories);
    const categoryOptions = flattenForSelect(byParent);

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };

    const handlePageSizeChange = (pageSize) => {
        setPageSize(pageSize);
        setPage(1);
    };

    const handleCategoryFilterChange = (event) => {
        setFilterItemCategoryId(event.target.value);
        setPage(1);
    };

    const handlePropertyValueFilterChange = () => {
        const selectedValues = Array.from(filterPropertyValueRef.current.selectedOptions).map((option) => option.value);
        setFilterPropertyValueIds(selectedValues);
        setPage(1);
    };

    const handleCreateButtonClick = () => {
        setIsUpdate(false);
        setEditingItem(null);
        setDescription('');
        setVariantRows([emptyVariant()]);
        chosenImageRef.current = null;
        setShowModal(true);
    };

    const handleEditIconClick = (item) => {
        setIsUpdate(true);
        setEditingItem(item);
        setDescription(item.description ?? '');
        chosenImageRef.current = null;
        setShowModal(true);
    };

    const handleShowIconClick = (item) => {
        navigate('/items/' + item.itemId + '/variants');
    };

    const handleDeleteIconClick = (item) => {
        if (!window.confirm('Are you sure you want to delete "' + item.name + '"? This archives it and cannot be undone from here.')) {
            return;
        }

        deleteItem(item.itemId)
            .then(() => {
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success('Success!');
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    // The API limits the image to 2 MB, so a phone photo is shrunk first.
    const handleImageChange = (event) => {
        const file = event.target.files[0];
        chosenImageRef.current = file ? compressImage(file) : null;
    };

    const submitItem = async (save) => {
        if (saving) {
            return;
        }

        setSaving(true);

        try {
            await save(await chosenImageRef.current);
            setShowModal(false);
            setSubmitCount((prevCount) => prevCount + 1);
            toast.success('Success!');
        }
        catch (error) {
            toast.error(propertySaveErrorMessage(error));
        }
        finally {
            setSaving(false);
        }
    };

    const readFormFields = () => ({
        itemCategoryId : itemCategoryIdRef.current.value,
        name           : nameRef.current.value,
        description    : description,
        bruttoPrice    : bruttoPriceRef.current.value,
        isPublic       : isPublicRef.current.checked,
        order          : parseInt(orderRef.current.value, 10) || 1
    });

    const handleCreateSubmit = (event) => {
        event.preventDefault();

        const fields = readFormFields();
        const variants = variantRows.map((row) => ({
            stockQuantity : parseInt(row.stockQuantity, 10) || 0,
            properties    : toPropertyList(row.properties)
        }));

        submitItem((image) => createItem(
            fields.itemCategoryId, fields.name, fields.description, fields.bruttoPrice, image, fields.isPublic, fields.order, variants
        ));
    };

    const handleEditSubmit = (event) => {
        event.preventDefault();

        const fields = readFormFields();

        submitItem((image) => updateItem(editingItem.itemId, fields, image));
    };

    const itemList = items.map((item) => ({
        raw          : item,
        itemId       : item.itemId,
        // image is what the shop displays: the item's own image, else a variant's.
        imageThumb   : <Thumbnail src={item.image} alt={item.name}/>,
        name         : item.name,
        categoryName : item.itemCategoryName,
        price        : item.bruttoPrice + ' ' + item.currency,
        stockTotal   : (item.variants || []).reduce((sum, variant) => sum + variant.stockQuantity, 0),
        isPublicText : item.isPublic ? 'Yes' : 'No',
        order        : item.order
    }));

    const listHeaders = [
        'Id',
        'Image',
        'Name',
        'Category',
        'Price',
        'Stock',
        'Public',
        'Order',
        'Actions'
    ];

    const columns = {
        functionParams : ['raw'],
        dataKeys       : [
            'itemId',
            'imageThumb',
            'name',
            'categoryName',
            'price',
            'stockTotal',
            'isPublicText',
            'order'
        ],
    };

    return <Fragment>
        <Box>
            <Button title='Create item' handleClick={handleCreateButtonClick}/>
            <div className="flex flex-wrap gap-3 my-4">
                <select
                    value={filterItemCategoryId}
                    onChange={handleCategoryFilterChange}
                    className="border p-2"
                >
                    <option value="">All categories</option>
                    {categoryOptions.map((option) => (
                        <option key={option.itemCategoryId} value={option.itemCategoryId}>
                            {'—'.repeat(option.depth)} {option.name}
                        </option>
                    ))}
                </select>
                <select
                    ref={filterPropertyValueRef}
                    multiple={true}
                    onChange={handlePropertyValueFilterChange}
                    className="border p-2"
                >
                    {propertyValueOptions.map((option) => (
                        <option key={option.itemPropertyValueId} value={option.itemPropertyValueId}>
                            {option.name}: {option.value}
                        </option>
                    ))}
                </select>
            </div>
            <ListEdit
                headers={listHeaders}
                items={itemList}
                columns={columns}
                pagination={pagination}
                handleEditIconClick={handleEditIconClick}
                handleShowIconClick={handleShowIconClick}
                handleDeleteIconClick={handleDeleteIconClick}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
            />
        </Box>
        {showModal && (
            <Modal>
                <form onSubmit={isUpdate ? handleEditSubmit : handleCreateSubmit}>
                    <ModalForm
                        title={isUpdate ? 'Update item' : 'Create item'}
                        saveButtonTitle={isUpdate ? 'Update' : 'Create'}
                        // Closing mid-save would let its later success close a modal opened since.
                        handleCancelButtonClick={saving ? undefined : () => setShowModal(false)}
                    >
                        <ModalFormInputRow title="Category">
                            {categoryOptions.length > 0 && (
                                <select
                                    ref={itemCategoryIdRef}
                                    defaultValue={isUpdate ? editingItem.itemCategoryId : categoryOptions[0].itemCategoryId}
                                    className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                                >
                                    {categoryOptions.map((option) => (
                                        <option key={option.itemCategoryId} value={option.itemCategoryId}>
                                            {'—'.repeat(option.depth)} {option.name}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Name'>
                            <input
                                ref={nameRef}
                                type="text"
                                defaultValue={isUpdate ? editingItem.name : ''}
                                placeholder="Enter name"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title="Description">
                            <CustomQuillEditor
                                value={description}
                                onChange={setDescription}
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Brutto price'>
                            <input
                                ref={bruttoPriceRef}
                                type="number"
                                step="0.01"
                                defaultValue={isUpdate ? editingItem.bruttoPrice : ''}
                                placeholder="Enter brutto price"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title="Image">
                            <input
                                type="file"
                                onChange={handleImageChange}
                                accept="image/jpeg,image/png,image/jpg,image/gif"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                            {/* itemImage, not image: the display image may be borrowed from a variant. */}
                            {isUpdate && editingItem.itemImage && (
                                <div className="mt-2">
                                    <img
                                        src={editingItem.itemImage}
                                        alt="Preview"
                                        className="w-24 h-24 object-cover border rounded"
                                    />
                                </div>
                            )}
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Order'>
                            <input
                                ref={orderRef}
                                type="number"
                                defaultValue={isUpdate ? editingItem.order : 1}
                                placeholder="Enter order"
                                className="border rounded-lg py-2 px-3 w-full text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        <ModalFormInputRow title='Is public'>
                            <input
                                ref={isPublicRef}
                                type="checkbox"
                                defaultChecked={isUpdate ? editingItem.isPublic : true}
                                className="border rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:shadow-outline"
                            />
                        </ModalFormInputRow>
                        {!isUpdate && (
                            <ModalFormInputRow title="Variants (at least one is required)">
                                <VariantRowsEditor variants={variantRows} onChange={setVariantRows}/>
                            </ModalFormInputRow>
                        )}
                        {isUpdate && (
                            <p className="text-sm text-gray-500">
                                Manage this item's variants and stock via the "View" (eye) icon on the list.
                            </p>
                        )}
                    </ModalForm>
                </form>
            </Modal>
        )}
    </Fragment>;
}

export default ItemList;
